export const GP3R_STORAGE_KEY = 'immoiq_gp3r_demo_profile';

export const demoProfiles = {
  beginner: {
    id: 'beginner',
    label: 'Débutant',
    shortLabel: 'Débutant',
    icon: 'Seedling',
    headline: 'Comprendre rapidement si un immeuble est intéressant',
    description: 'Explications simples, verdict clair et indicateurs essentiels.',
    guidanceTone: 'On met l’accent sur les bases : cashflow, mise de fonds et niveau de risque.',
    resultIntro: 'Voici la lecture simple de l’immeuble. L’objectif est de voir si le projet tient debout sans entrer dans tous les détails techniques.',
  },
  intermediate: {
    id: 'intermediate',
    label: 'Intermédiaire',
    shortLabel: 'Intermédiaire',
    icon: 'BarChart3',
    headline: 'Comparer les ratios et les scénarios de financement',
    description: 'Ratios de rentabilité, cashflow, taux et sensibilité.',
    guidanceTone: 'On ajoute les ratios utiles pour comparer cette opportunité avec d’autres immeubles.',
    resultIntro: 'Le deal est analysé avec les ratios clés pour décider s’il mérite une offre ou une négociation.',
  },
  advanced: {
    id: 'advanced',
    label: 'Avancé',
    shortLabel: 'Avancé',
    icon: 'LineChart',
    headline: 'Tester la robustesse du deal et les leviers d’optimisation',
    description: 'DSCR, stress test, leviers, risques et projections.',
    guidanceTone: 'On pousse l’analyse sur la dette, la sensibilité aux taux et les leviers d’amélioration.',
    resultIntro: 'Cette vue met l’accent sur la résilience du montage, les risques et les actions à prioriser.',
  },
};

export const demoPortfolio = [
  {
    id: 'demo-duplex',
    address: 'Duplex Sainte-Cécile',
    city: 'Trois-Rivières',
    type: 'Duplex',
    units: 2,
    value: 428000,
    monthlyCashflow: 420,
    equity: 112000,
    score: 74,
    note: 'Stable, bon historique locatif',
  },
  {
    id: 'demo-triplex',
    address: 'Triplex Laviolette',
    city: 'Trois-Rivières',
    type: 'Triplex',
    units: 3,
    value: 612000,
    monthlyCashflow: 735,
    equity: 158000,
    score: 81,
    note: 'Potentiel de hausse des loyers',
  },
  {
    id: 'demo-sixplex',
    address: '6 logements Cap-de-la-Madeleine',
    city: 'Trois-Rivières',
    type: 'Multilogement',
    units: 6,
    value: 1185000,
    monthlyCashflow: 1460,
    equity: 287000,
    score: 78,
    note: 'Gestion plus active, rendement solide',
  },
];

export const demoNewProperty = {
  address: 'Triplex GP3R - Rue Notre-Dame',
  city: 'Trois-Rivières',
  type: 'Triplex',
  units: 3,
  askingPrice: 575000,
  estimatedValue: 565000,
  monthlyRevenue: 5100,
  monthlyExpenses: 1830,
  mortgagePayment: 2780,
  downPayment: 115000,
  closingCosts: 10800,
  interestRate: 5.39,
  amortization: 25,
  vacancyRisk: 'Modéré',
  neighborhood: 'Près du centre-ville et du circuit GP3R',
};

export const guidedSteps = [
  {
    id: 'property',
    title: 'Immeuble sélectionné',
    icon: 'Building2',
    description: 'ImmoIQ charge une fiche démo déjà remplie : adresse, revenus, dépenses et financement.',
  },
  {
    id: 'analysis',
    title: 'Analyse automatique',
    icon: 'Sparkles',
    description: 'Aucune saisie manuelle : le mode démo simule le calcul de cashflow, rendement et risque.',
  },
  {
    id: 'results',
    title: 'Résultat adapté',
    icon: 'BadgeCheck',
    description: 'La page résultat ajuste les explications selon le niveau choisi au début.',
  },
];

export const profileResults = {
  beginner: {
    verdict: 'Intéressant, mais à valider',
    score: 72,
    tone: 'balanced',
    summary: 'Le triplex génère un cashflow net positif, même avec une hypothèque réaliste sur 25 ans. Pour un premier regard, le projet est compréhensible et mérite une validation terrain.',
    kpis: [
      { label: 'Cashflow net', value: '490 $/mois', helper: 'Montant restant après revenus, dépenses et hypothèque', icon: 'Wallet' },
      { label: 'Rendement annuel', value: '4,7 %', helper: 'Cashflow annuel estimé sur le capital investi', icon: 'Percent' },
      { label: "Valeur à la fin de l'hypothèque", value: '1 184 000 $', helper: 'Valeur projetée après 25 ans à 3 % de croissance', icon: 'Home' },
    ],
    insights: [
      'Le cashflow est positif, ce qui rend le projet plus confortable pour commencer.',
      'La valeur projetée montre le potentiel long terme, mais elle dépend de l’entretien et du marché.',
      'Avant d’acheter, il faut valider les baux, les taxes, les assurances et l’état du bâtiment.',
    ],
    nextActions: [
      'Demander les baux et les dépenses réelles.',
      'Vérifier l’inspection du bâtiment.',
      'Comparer avec un autre triplex du secteur.',
    ],
  },
  intermediate: {
    verdict: 'Bon candidat pour négociation',
    score: 79,
    tone: 'good',
    summary: 'Le rendement est correct au prix affiché, mais le deal devient plus attrayant si le prix baisse ou si un logement peut être optimisé.',
    kpis: [
      { label: 'Cashflow', value: '490 $/mois', helper: 'Cashflow net après financement', icon: 'Wallet' },
      { label: 'ROI cash-on-cash', value: '4,7 %', helper: 'Cashflow annuel / capital investi', icon: 'Percent' },
      { label: 'ROI total annuel', value: '8,5 %/an', helper: 'Rendement annuel moyen incluant cashflow, capital et appréciation', icon: 'TrendingUp' },
      { label: 'Ratio couverture de dette', value: '1,18x', helper: 'NOI / service de dette annuel', icon: 'ShieldCheck' },
      { label: 'Taux de capitalisation', value: '6,8 %', helper: 'NOI annualisé / prix demandé', icon: 'BarChart3' },
    ],
    projectionTitle: 'Projection simple sur la durée du prêt',
    projection: [
      { year: 1, propertyValue: 591000, mortgageBalance: 452000, equity: 139000, annualROI: 5.0 },
      { year: 5, propertyValue: 665000, mortgageBalance: 416000, equity: 249000, annualROI: 6.1 },
      { year: 10, propertyValue: 771000, mortgageBalance: 357000, equity: 414000, annualROI: 7.0 },
      { year: 15, propertyValue: 894000, mortgageBalance: 280000, equity: 614000, annualROI: 8.1 },
      { year: 20, propertyValue: 1037000, mortgageBalance: 176000, equity: 861000, annualROI: 9.0 },
      { year: 25, propertyValue: 1184000, mortgageBalance: 0, equity: 1184000, annualROI: 10.1 },
    ],
    insights: [
      'Le DSCR est au-dessus de 1, mais la marge reste serrée si les dépenses réelles dépassent le budget.',
      'Une baisse de prix de 20 000 $ améliore nettement le rendement sur capital.',
      'La sensibilité aux taux reste importante : à 6,25 %, le cashflow devient presque neutre sans optimisation.',
    ],
    nextActions: [
      'Tester une offre à 555 000 $.',
      'Valider les loyers comparables du secteur.',
      'Simuler un renouvellement hypothécaire à taux plus élevé.',
    ],
  },
  advanced: {
    verdict: 'Achetable avec structure prudente',
    score: 83,
    tone: 'strong',
    summary: 'Le dossier est viable si la dette est structurée prudemment. Le solde hypothécaire descend graduellement jusqu’à 0 $ sur 25 ans, et le rendement total provient surtout du remboursement de capital et de l’appréciation.',
    kpis: [
      { label: 'Cashflow net', value: '490 $/mois', helper: 'Après dette, dépenses et réserve minimale', icon: 'Wallet' },
      { label: 'ROI total moyen', value: '8,5 %/an', helper: 'Rendement annualisé incluant cashflow, dette et appréciation', icon: 'LineChart' },
      { label: 'Ratio couverture de dette', value: '1,18x', helper: 'NOI / service de dette annuel', icon: 'ShieldCheck' },
      { label: 'Taux de capitalisation', value: '6,8 %', helper: 'NOI annualisé / prix demandé', icon: 'BarChart3' },
      { label: 'Prix par porte', value: '191 667 $', helper: 'Prix demandé divisé par les 3 logements', icon: 'DoorOpen' },
      { label: 'Détail du rendement total', value: 'Cashflow 12 % • Capital 38 % • Appréciation 50 %', helper: 'Composition estimée sur 25 ans', icon: 'PieChart' },
      { label: 'Break-even', value: 'Année 6', helper: 'Année où le cumul devient positif après capital investi', icon: 'TimerReset' },
    ],
    projectionTitle: 'Projection avancée sur la durée du prêt',
    projection: [
      { year: 1, propertyValue: 591000, mortgageBalance: 452000, equity: 139000, annualROI: 5.0, cumulativeReturn: 6000 },
      { year: 5, propertyValue: 665000, mortgageBalance: 416000, equity: 249000, annualROI: 6.1, cumulativeReturn: 93000 },
      { year: 10, propertyValue: 771000, mortgageBalance: 357000, equity: 414000, annualROI: 7.0, cumulativeReturn: 258000 },
      { year: 15, propertyValue: 894000, mortgageBalance: 280000, equity: 614000, annualROI: 8.1, cumulativeReturn: 458000 },
      { year: 20, propertyValue: 1037000, mortgageBalance: 176000, equity: 861000, annualROI: 9.0, cumulativeReturn: 705000 },
      { year: 25, propertyValue: 1184000, mortgageBalance: 0, equity: 1184000, annualROI: 10.1, cumulativeReturn: 1029000 },
    ],
    returnBreakdown: [
      { label: 'Cashflow cumulé', value: 147000, percent: 12 },
      { label: 'Capital remboursé', value: 460000, percent: 38 },
      { label: 'Appréciation', value: 609000, percent: 50 },
    ],
    planningPreview: [
      { year: 'Année 1-2', title: 'Stabiliser', detail: 'Confirmer les dépenses, garder une réserve et sécuriser les baux.' },
      { year: 'Année 3-5', title: 'Optimiser', detail: 'Rénovations légères, hausse graduelle du NOI et amélioration du DSCR.' },
      { year: 'Année 6+', title: 'Planifier', detail: 'Comparer conservation, refinancement ou achat d’un deuxième immeuble.' },
    ],
    insights: [
      'La marge de dette est acceptable, mais le stress test montre peu de coussin à taux élevé.',
      'La création de valeur dépend plus de l’optimisation locative que de l’appréciation.',
      'Le risque principal est une combinaison vacance + dépenses majeures dans les 24 premiers mois.',
    ],
    nextActions: [
      'Négocier une retenue ou un ajustement selon inspection.',
      'Valider une stratégie de refinancement après stabilisation.',
      'Prioriser les améliorations qui augmentent le NOI sans CAPEX lourd.',
    ],
  },
};

export const portfolioSummary = {
  totalValue: 2225000,
  totalEquity: 557000,
  monthlyCashflow: 2615,
  averageScore: 78,
};
